import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, ArrowRight, ArrowLeft, X, RotateCcw } from 'lucide-react';
import Logo from './Logo';
import ProjectScopeFieldQuestion from './ProjectScopeFieldQuestion';
import { resolveNextRef, isAnswered, validateField, buildReadableAnswers, reorderContactFieldsFirst } from '../utils/projectScopeForm';

// Schema (questions + branching logic) is pulled from Typeform and cached server-side
// (see backend/services/typeformSchema.js) - this component never talks to Typeform
// directly, and never sends answers anywhere but our own /api/founding-application.

const STORAGE_KEY = 'tuneup_founding_form';

function loadDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveDraft(currentRef, history, valuesByRef) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ currentRef, history, valuesByRef }));
  } catch {}
}

function clearDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

const FoundingForm = ({ onClose, onSuccess }) => {
  const [schema, setSchema] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [currentRef, setCurrentRef] = useState(null);
  const [history, setHistory] = useState([]);
  const [valuesByRef, setValuesByRef] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const scrollContainerRef = useRef(null);

  const loadSchema = useCallback(() => {
    setLoadError('');
    setSchema(null);
    setCurrentRef(null);
    fetch('/api/founding-form/schema')
      .then((res) => {
        if (!res.ok) throw new Error('Could not load the application form');
        return res.json();
      })
      .then((data) => {
        const reordered = reorderContactFieldsFirst(data);
        const firstRef = reordered.fields[0]?.ref ?? null;
        const draft = loadDraft();
        if (draft && draft.currentRef && reordered.fields.some((f) => f.ref === draft.currentRef)) {
          setSchema(reordered);
          setCurrentRef(draft.currentRef);
          setHistory(draft.history || []);
          setValuesByRef(draft.valuesByRef || {});
          setHasDraft(true);
        } else {
          // Set both atomically so there's never a render where schema is set but currentRef isn't
          setSchema(reordered);
          setCurrentRef(firstRef);
        }
      })
      .catch(() => setLoadError('Could not load the application form. Please try again shortly.'));
  }, []);

  useEffect(() => { loadSchema(); }, [loadSchema]);

  const currentField = schema?.fields.find((f) => f.ref === currentRef);
  const currentValue = currentRef ? valuesByRef[currentRef] : undefined;

  // The modal's own content area scrolls (max-h-[90vh] overflow-y-auto), and that
  // scroll position doesn't reset on its own when the question changes - on mobile,
  // where a long question can push the Continue button below the fold, advancing
  // left the next question's title scrolled out of view above the visible area.
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setShowValidation(false);
  }, [currentRef]);

  // Persist to localStorage on every state change
  useEffect(() => {
    if (!currentRef) return;
    saveDraft(currentRef, history, valuesByRef);
  }, [currentRef, history, valuesByRef]);

  const setValue = useCallback((ref, value) => {
    setValuesByRef((prev) => ({ ...prev, [ref]: value }));
  }, []);

  const goNext = () => {
    if (!schema || !currentField) return;
    if (!isAnswered(currentField, currentValue)) return;
    const fieldError = validateField(currentField, currentValue);
    if (fieldError) { setShowValidation(true); return; }

    const nextRef = resolveNextRef(currentRef, valuesByRef, schema);
    setHistory((prev) => [...prev, currentRef]);

    if (!nextRef) {
      submit();
      return;
    }
    setCurrentRef(nextRef);
  };

  const goBack = () => {
    if (history.length === 0) return;
    const previous = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setCurrentRef(previous);
  };

  const submit = async () => {
    const answers = buildReadableAnswers(schema, valuesByRef);

    const byKey = (subfieldTitleGuess) =>
      schema.fields.find((f) => f.title.toLowerCase() === subfieldTitleGuess)?.ref;

    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await fetch('/api/founding-application', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: [valuesByRef[byKey('first name')], valuesByRef[byKey('last name')]].filter(Boolean).join(' '),
          email: valuesByRef[byKey('email')],
          phone: valuesByRef[byKey('phone number')],
          company: valuesByRef[byKey('company')],
          answers,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setSubmitError(data.error || 'Failed to submit application');
        return;
      }
      clearDraft();
      onSuccess({ projectId: data.projectId });
    } catch (err) {
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // history.length / schema.fields.length undercounts badly: schema.fields holds every
  // field across every industry branch, but a respondent only ever traverses one branch,
  // so that denominator is far bigger than any real path and the bar never reaches 100%.
  // Logic jumps in this form always skip forward (never back to an earlier field), and
  // the contact_info block is always the last entry, so position-in-document-order hits
  // exactly 100% when the respondent reaches the final field instead.
  const currentIndex = schema && currentRef ? schema.fields.findIndex((f) => f.ref === currentRef) : 0;
  const progress = schema && schema.fields.length > 1 ? Math.round((currentIndex / (schema.fields.length - 1)) * 100) : 0;
  const isLastField = schema && currentField ? resolveNextRef(currentRef, valuesByRef, schema) === null : false;

  const hasProgress = history.length > 0 || Object.keys(valuesByRef).length > 0;

  const requestClose = () => {
    if (hasProgress) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  };

  const confirmDiscard = () => {
    clearDraft();
    onClose();
  };

  return createPortal(
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <button
        type="button"
        onClick={requestClose}
        aria-label="Close"
        className="fixed top-4 right-4 z-[60] text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
      <motion.div
        ref={scrollContainerRef}
        className="relative bg-slate-900 border border-white/10 rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
      >
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <Logo size="large" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Tell Us About Your Project</h3>
          <p className="text-gray-400 text-sm">A few questions so we understand what you're building</p>
        </div>

        {hasDraft && (
          <div className="bg-orange-600/10 border border-orange-500/30 rounded-lg p-3 flex items-center gap-3 mb-4">
            <RotateCcw className="w-4 h-4 text-orange-400 shrink-0" />
            <span className="text-orange-200 text-sm flex-1">We saved your progress. Picking up where you left off.</span>
          </div>
        )}

        {loadError && (
          <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-3 flex items-center mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 mr-2 flex-shrink-0" />
            <span className="text-red-400 text-sm">{loadError}</span>
          </div>
        )}

        {!schema && !loadError && (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-white/30 border-t-orange-400 rounded-full animate-spin mx-auto" />
          </div>
        )}

        {schema && !currentField && !loadError && (
          <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-3 flex items-center justify-between mb-4">
            <div className="flex items-center">
              <AlertCircle className="w-4 h-4 text-red-400 mr-2 flex-shrink-0" />
              <span className="text-red-400 text-sm">Could not load questions. Please try again.</span>
            </div>
            <button onClick={loadSchema} className="text-orange-400 hover:text-orange-300 text-sm font-semibold ml-4 shrink-0">Retry</button>
          </div>
        )}

        {schema && currentField && (
          <>
            <div className="h-1 bg-white/10 rounded-full mb-8 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-red-600 to-orange-600 transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentRef}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.2 }}
              >
                <ProjectScopeFieldQuestion field={currentField} value={currentValue} onChange={(v) => { setValue(currentField.ref, v); setShowValidation(false); }} onSubmitOnEnter={goNext} onAutoAdvance={goNext} error={showValidation ? validateField(currentField, currentValue) : null} />
              </motion.div>
            </AnimatePresence>

            {submitError && (
              <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-3 flex items-center mt-4">
                <AlertCircle className="w-4 h-4 text-red-400 mr-2 flex-shrink-0" />
                <span className="text-red-400 text-sm">{submitError}</span>
              </div>
            )}

            <div className="flex items-center justify-between mt-8">
              <button
                onClick={goBack}
                disabled={history.length === 0}
                className="text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 text-sm flex items-center transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </button>
              <motion.button
                onClick={goNext}
                disabled={!isAnswered(currentField, currentValue) || isSubmitting}
                className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    {isLastField ? 'Submit' : 'Continue'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </motion.button>
            </div>
          </>
        )}

        <button onClick={requestClose} className="mt-6 w-full text-center text-gray-400 hover:text-white text-sm transition-colors">
          Cancel
        </button>

        {/* Discard confirmation overlay */}
        <AnimatePresence>
          {showConfirm && (
            <motion.div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-slate-900/95 backdrop-blur-sm p-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="text-center">
                <div className="w-12 h-12 bg-orange-600/20 border border-orange-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-6 h-6 text-orange-400" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2">Cancel your application?</h4>
                <p className="text-gray-400 text-sm mb-6">Your progress is saved — you can come back and continue anytime. Or discard everything and start fresh next time.</p>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                  >
                    Keep going
                  </button>
                  <button
                    onClick={onClose}
                    className="w-full border border-gray-700 hover:border-gray-500 text-gray-300 hover:text-white text-sm font-medium transition-colors py-2.5 rounded-lg"
                  >
                    Save & close — continue later
                  </button>
                  <button
                    onClick={confirmDiscard}
                    className="w-full text-red-400 hover:text-red-300 text-sm transition-colors py-2"
                  >
                    Discard everything and close
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default FoundingForm;
